import { Ball, COLORS, INITIALIZED_PINS } from './config';
import { LoadedGameAssets } from '../assetConfig';
import { globalAssetsRef } from '../assetStore';
import { CustomSpriteAnimator } from '../components/CustomSpriteAnimator';
import { Sprite } from '../components/Sprite';
import {
  PIN_RADIUS_BASE,
  SPECIAL_PIN_RADIUS_BASE,
  BALL_RADIUS_BASE,
  PIN_SPACING_X_BASE,
  BIN_MULTIPLIERS,
  isGoldenBall,
  getBinLabel,
  CANVAS_WIDTH,
  CANVAS_HEIGHT,
  DEVICE_PIXEL_RATIO,
  PIN_SPARK_MAX_FRAMES,
} from './constants';
import type { BlinkoMode } from './constants';

// Rendering state
export type LetterRenderState = {
  letter: string;
  x: number;
  y: number;
  active: boolean;
};

type LetterAnimationState = {
  active: boolean;
  appearing: boolean;
  appearElapsed: number;
  activating: boolean;
  activationElapsed: number;
  rotationDir: number;
};

type BeeSparkleInstance = {
  animator: CustomSpriteAnimator;
  active: boolean;
  speed: number;
  vx: number;
  vy: number;
  elapsed: number;
  duration: number;
};

export type RenderState = {
  hitBins: Set<number>;
  pinFx: Map<number, number>;
  letters?: LetterRenderState[];
  mode?: BlinkoMode;
  hardMode?: boolean;
};

// Draw rounded rectangle utility
export const drawRoundedRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
): void => {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
};

export class BlinkoRenderer {
  private scale: number = 1;

  // Precomputed static sprites
  private pinSprites: (Sprite | null)[] = [];
  private binHoleSprites: Sprite[] = [];
  private binDividerSprites: Sprite[] = [];
  private binLightBeams: (CustomSpriteAnimator | null)[] = [];
  // Bee sparkle pool
  private beeSparkles: BeeSparkleInstance[] = [];
  private beeSparkleLength: number = 0;
  private readonly beeSparklePoolSize = 12;
  private readonly beeSparkleBurstSize = 3;
  private beeSparklePendingBursts: number[] = []; // Queue of bin indices
  private beeSparkleActiveCount = 0;
  private beeSparkleIdleAnimation: string = 'idle';

  private mode: BlinkoMode = 'main';
  private hardMode: boolean = false;

  private readonly boardYOffset = 200;
  private readonly letterAppearDuration = 0.3;
  private readonly letterActivateDuration = 0.25;
  private letterAnimationStates = new Map<string, LetterAnimationState>();

  // Track initialization of lazy assets
  private assetsInitialized = {
     lightBeams: false,
     beeSparkles: false,
     pins: false,
  };

  setScale(scale: number) {
    this.scale = scale;
  }

  private checkAndInitAssets() {
    const assets = globalAssetsRef.current;
    if (!assets) return;

    this.initBeeSparkles(assets);
    this.initPins(assets);
    this.initLightBeams(assets);
  }

  private initBeeSparkles(assets: LoadedGameAssets) {
    if (this.assetsInitialized.beeSparkles || !assets.customAnimations?.['bee-sparkling']) return;

    this.prepareBeeSparkles();
    this.assetsInitialized.beeSparkles = true;
  }

  private initPins(assets: LoadedGameAssets) {
    if (this.assetsInitialized.pins || Object.keys(assets.images).length === 0) return;

    this.computeStaticSprites();
    if (this.pinSprites.length > 0) {
          this.assetsInitialized.pins = true;
    }
  }

  private initLightBeams(assets: LoadedGameAssets) {
    if (this.assetsInitialized.lightBeams || !assets.customAnimations?.['light-beam']) return;

    this.prepareBinLightBeams();
    this.assetsInitialized.lightBeams = true;
  }

  update(dt: number) {
    this.checkAndInitAssets();
    if (this.binLightBeams.length) {
      this.binLightBeams.forEach(anim => anim?.update(dt));
    }
    this.updateBeeSparkles(dt);
    this.advanceLetterAnimations(dt);
  }

  render(ctx: CanvasRenderingContext2D, balls: Ball[], state: RenderState) {
    const hitBins = state.hitBins;
    const pinFx = state.pinFx;
    const letters = state.letters ?? [];
    const mode = state.mode ?? this.mode;
    const hardMode = state.hardMode ?? false;
    this.mode = mode;
    this.hardMode = hardMode;

    const isMobile = window.innerWidth < 500;
    const scale = this.scale * DEVICE_PIXEL_RATIO * (isMobile ? 1.18 : 0.84);

    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    
    // Background is now handled by React component (BackgroundLayer)
    
    ctx.save();
    ctx.scale(scale, scale);

    // Translate the main game layer: center horizontally + vertical adjustment
    ctx.save();
    const viewWidth = ctx.canvas.width / scale;
    const viewHeight = ctx.canvas.height / scale;
    const dx = (viewWidth - CANVAS_WIDTH) / 2;
    const dy = (viewHeight - CANVAS_HEIGHT) / 2;

    const mobileBottomPadding = isMobile ? 215 : 97; 
    ctx.translate(dx, dy - mobileBottomPadding); // anchor game area to bottom when extra vertical space exists

    ctx.save();
    // Board is now handled by React component (BoardLayer)
    
    this.renderPinsCached(ctx, pinFx);
    this.renderBinsCached(ctx);
    if (this.mode === 'main') {
      this.renderLetters(ctx, letters);
    } else {
      this.letterAnimationStates.clear();
    }

    this.renderBalls(ctx, balls);

    this.renderBeeSparkles(ctx);
    this.renderBinLightBeams(ctx, hitBins); // Moved here to test Z-order
    ctx.restore();

    // Result scene is now handled by React component (ResultModal)

    ctx.restore();
    ctx.restore();
  }

  private renderLetters(ctx: CanvasRenderingContext2D, letters: LetterRenderState[]) {
    if (!globalAssetsRef.current) return;
    this.syncLetterAnimations(letters);
    if (!letters.length) return;

    ctx.save();

    letters.forEach((letterState) => {
      const { letter, x, y } = letterState;
      const drawX = x;
      const drawY = y + this.boardYOffset;
      const key = this.getLetterKey(letterState);
      const animState = this.letterAnimationStates.get(key);

      const offKey = `letters-off-${letter.toLowerCase()}`;
      const offImg = globalAssetsRef.current!.images[offKey];
      if (!offImg) return;

      const baseWidth = offImg.width / 8;
      const baseHeight = offImg.height / 8;
      const appearScale = animState ? this.computeAppearScale(animState) : 1;
      const appearRotation = animState ? this.computeAppearRotation(animState) : 0;

      ctx.save();
      ctx.translate(drawX, drawY);
      if (appearRotation !== 0) {
        ctx.rotate(appearRotation);
      }
      ctx.scale(appearScale, appearScale);
      ctx.drawImage(offImg, -baseWidth / 2, -baseHeight / 2, baseWidth, baseHeight);
      ctx.restore();

      if (!animState?.active) {
        return;
      }

      const onKey = `letters-on-${letter.toLowerCase()}`;
      const onImg = globalAssetsRef.current!.images[onKey];
      if (!onImg) return;

      const onWidth = onImg.width / 8;
      const onHeight = onImg.height / 8;
      const activationScale = (animState ? this.computeActivationScale(animState) : 1) * appearScale;

      ctx.save();
      ctx.translate(drawX, drawY);
      ctx.scale(activationScale, activationScale);
      ctx.drawImage(onImg, -onWidth / 2, -onHeight / 2, onWidth, onHeight);
      ctx.restore();
    });

    ctx.restore();
  }

  private syncLetterAnimations(letters: LetterRenderState[]) {
    if (!letters.length) {
      this.letterAnimationStates.clear();
      return;
    }

    const nextKeys = new Set<string>();
    letters.forEach((letterState) => {
      const key = this.getLetterKey(letterState);
      nextKeys.add(key);
      const existing = this.letterAnimationStates.get(key);
      if (!existing) {
        const rotationDir = letterState.letter.charCodeAt(0) % 2 === 0 ? 1 : -1;
        this.letterAnimationStates.set(key, {
          active: letterState.active,
          appearing: true,
          appearElapsed: 0,
          activating: letterState.active,
          activationElapsed: 0,
          rotationDir,
        });
        return;
      }

      const becameActive = letterState.active && !existing.active;
      const lostActivation = !letterState.active && existing.active;
      existing.active = letterState.active;
      if (becameActive) {
        existing.activating = true;
        existing.activationElapsed = 0;
      } else if (lostActivation) {
        existing.activating = false;
        existing.activationElapsed = 0;
      }
    });

    Array.from(this.letterAnimationStates.keys()).forEach((key) => {
      if (!nextKeys.has(key)) {
        this.letterAnimationStates.delete(key);
      }
    });
  }

  private advanceLetterAnimations(dt: number) {
    if (!this.letterAnimationStates.size) return;

    this.letterAnimationStates.forEach((state) => {
      if (state.appearing) {
        state.appearElapsed = Math.min(state.appearElapsed + dt, this.letterAppearDuration);
        if (state.appearElapsed >= this.letterAppearDuration) {
          state.appearing = false;
        }
      }

      if (state.activating) {
        state.activationElapsed = Math.min(state.activationElapsed + dt, this.letterActivateDuration);
        if (state.activationElapsed >= this.letterActivateDuration) {
          state.activating = false;
          state.activationElapsed = this.letterActivateDuration;
        }
      } else if (!state.active) {
        state.activationElapsed = 0;
      } else if (state.activationElapsed < this.letterActivateDuration) {
        state.activationElapsed = this.letterActivateDuration;
      }
    });
  }

  private getLetterKey(letter: LetterRenderState) {
    return `${letter.letter}-${Math.round(letter.x)}-${Math.round(letter.y)}`;
  }

  private computeAppearScale(state: LetterAnimationState): number {
    if (!state.appearing) return 1;
    const t = this.clamp01(state.appearElapsed / this.letterAppearDuration);
    const eased = this.easeOutBack(t);
    return 0.7 + 0.3 * eased;
  }

  private computeAppearRotation(state: LetterAnimationState): number {
    if (!state.appearing) return 0;
    const t = this.clamp01(state.appearElapsed / this.letterAppearDuration);
    const eased = this.easeOutQuad(t);
    const maxAngle = this.degToRad(6);
    return maxAngle * state.rotationDir * (1 - eased);
  }

  private computeActivationScale(state: LetterAnimationState): number {
    if (!state.active) return 1;
    if (!state.activating) return 1;
    const t = this.clamp01(state.activationElapsed / this.letterActivateDuration);
    const eased = this.easeOutCubic(t);
    const startScale = 1.5;
    return startScale - (startScale - 1) * eased;
  }

  private clamp01(value: number): number {
    if (value <= 0) return 0;
    if (value >= 1) return 1;
    return value;
  }

  private easeOutQuad(t: number): number {
    const inv = 1 - t;
    return 1 - inv * inv;
  }

  private easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
  }

  private easeOutBack(t: number): number {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    const inv = t - 1;
    return 1 + c3 * inv * inv * inv + c1 * inv * inv;
  }

  private degToRad(degrees: number): number {
    return (degrees * Math.PI) / 180;
  }

  private renderBalls(ctx: CanvasRenderingContext2D, balls: Ball[]) {
    const offset = this.boardYOffset;
    balls.forEach((ball) => {
      if (ball.x !== undefined && ball.y !== undefined) {
        const scaledX = ball.x;
        const scaledY = ball.y + offset;
        // A ball that has already earned the golden threshold is drawn hot, with a halo — the
        // player can watch it cross over mid-flight. Off unless v3 enables it.
        const golden = isGoldenBall(ball.currentSpecialHits);
        const gradient = ctx.createRadialGradient(
          scaledX - BALL_RADIUS_BASE * 0.3,
          scaledY - BALL_RADIUS_BASE * 0.3,
          0,
          scaledX,
          scaledY,
          BALL_RADIUS_BASE
        );
        if (golden) {
          gradient.addColorStop(0, '#FFFDF0');
          gradient.addColorStop(0.45, '#FFD84D');
          gradient.addColorStop(1, '#FF9B00');
        } else {
          gradient.addColorStop(0, '#FFBE0C');
          gradient.addColorStop(1, '#FFC685');
        }

        if (golden) {
          ctx.save();
          ctx.shadowColor = 'rgba(255, 200, 40, 0.95)';
          ctx.shadowBlur = BALL_RADIUS_BASE * 2.2;
        }
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(scaledX, scaledY, golden ? BALL_RADIUS_BASE * 1.15 : BALL_RADIUS_BASE, 0, Math.PI * 2);
        ctx.fill();
        if (golden) ctx.restore();

        ctx.strokeStyle = golden ? '#FFF3B0' : '#FFA500';
        ctx.lineWidth = golden ? 1.4 : 0.5;
        ctx.stroke();
      }
    });
  }

  // Trigger bee sparkle at a given bin index if available
  public notifyBinScored(binIndex: number) {
    if (this.hardMode && this.mode === 'main') return;

    if (binIndex === 0 || binIndex === BIN_MULTIPLIERS.length - 1) {
      this.spawnBeeSparkleBurst(binIndex);
    }
  }

  private updateBeeSparkles(dt: number) {
    if (!this.beeSparkles.length) return;

    this.beeSparkles.forEach((entry) => {
      entry.animator.update(dt);
      if (!entry.active) return;

      entry.elapsed += dt;
      entry.animator.x += entry.vx * dt;
      entry.animator.y += entry.vy * dt;

      if (entry.elapsed >= entry.duration) {
        this.deactivateBeeSparkle(entry);
      }
    });

    // Check if we can spawn pending bursts
    if (this.beeSparklePendingBursts.length > 0) {
      // Check if we have enough inactive bees for a burst
      const availableCount = this.beeSparkles.filter(s => !s.active).length;
      if (availableCount >= this.beeSparkleBurstSize) {
        const nextBinIndex = this.beeSparklePendingBursts.shift();
        if (nextBinIndex !== undefined) {
          this.activateBeeSparkleBurst(nextBinIndex);
        }
      }
    }
  }

  private spawnBeeSparkleBurst(binIndex: number) {
    if (!this.beeSparkles.length) return;

    // Check if we have enough inactive bees for a burst immediately
    const availableCount = this.beeSparkles.filter(s => !s.active).length;
    
    if (availableCount >= this.beeSparkleBurstSize) {
      this.activateBeeSparkleBurst(binIndex);
    } else {
      this.beeSparklePendingBursts.push(binIndex);
    }
  }

  private activateBeeSparkleBurst(binIndex: number) {
    if (!this.beeSparkles.length) return;

    const available = this.beeSparkles.filter((s) => !s.active);
    const targetCount = Math.min(this.beeSparkleBurstSize, available.length);
    if (targetCount === 0) return;

    const spacing = 80;
    for (let i = 0; i < targetCount; i++) {
      this.configureBeeSparkle(available[i], i, spacing, binIndex);
    }
    this.beeSparkleActiveCount += targetCount;
  }

  private configureBeeSparkle(instance: BeeSparkleInstance, index: number, spacing: number, binIndex: number) {
    const path = this.getBeeSparklePath(index, spacing, binIndex);

    instance.speed = path.speed;
    instance.vx = path.vx;
    instance.vy = path.vy;
    instance.elapsed = 0;
    instance.duration = path.duration;
    instance.animator.x = path.spawnX;
    instance.animator.y = path.spawnY;
    
    // Rotation adjustment:
    // Math.atan2(vy, vx) gives the direction of movement.
    // Add Math.PI (180 degrees) to flip it if the sprite is facing left.
    const rotationOffset = Math.PI; 
    instance.animator.rotation = Math.atan2(instance.vy, instance.vx) + rotationOffset;

    // Flip Y scale if flying from Left to Right (binIndex < 8) to prevent being upside down
    const isLeft = binIndex < BIN_MULTIPLIERS.length / 2;
    instance.animator.scaleY = isLeft ? -1 : 1;
    
    instance.animator.play(this.beeSparkleIdleAnimation, true);
    instance.animator.hidden = false;
    instance.active = true;
  }

  private getBeeSparklePath(index: number, spacing: number, binIndex: number) {
    // Speed up: reduce duration (was 2.0)
    const duration = 2; 

    // Use the sprite length (or a safe default) as padding to ensure it fully exits
    const padding = Math.max(1600, this.beeSparkleLength || 1600);

    // Determine direction based on bin index
    const isLeft = binIndex < BIN_MULTIPLIERS.length / 2;

    let startCenterX, startCenterY, targetCenterX, targetCenterY;

    // Reduce start offset so it appears sooner (was padding)
    const startOffset = 0; 

    if (isLeft) {
      // Fly from Bottom-Left to Top-Right
      startCenterX = -startOffset; // Start at left edge
      startCenterY = CANVAS_HEIGHT + startOffset; // Start at bottom edge
      
      targetCenterX = CANVAS_WIDTH + padding; // End off-screen right
      targetCenterY = -padding; // End off-screen top
    } else {
      // Fly from Bottom-Right to Top-Left (Default)
      startCenterX = CANVAS_WIDTH + startOffset; // Start at right edge
      startCenterY = CANVAS_HEIGHT + startOffset; // Start at bottom edge
      
      targetCenterX = -padding; // End off-screen left
      targetCenterY = -padding; // End off-screen top
    }

    // Calculate direction vector (Unit vector u)
    const dx = targetCenterX - startCenterX;
    const dy = targetCenterY - startCenterY;
    const len = Math.hypot(dx, dy);
    const uX = dx / len;
    const uY = dy / len;

    // Calculate normal vector (Unit vector n, perpendicular to u)
    // Rotated 90 degrees: (x, y) -> (-y, x)
    const nX = -uY;
    const nY = uX;

    // Calculate Offsets
    // 1. Lateral Offset: Spread bees out perpendicular to the path
    //    (index - 1) centers the group: 0 -> -1 (Left), 1 -> 0 (Center), 2 -> 1 (Right)
    const lateralOffset = (index - 1) * spacing;

    // 2. Forward Offset: Move the leader (index 1) forward along the path
    //    We add this to the position along the direction vector u
    const forwardOffset = index === 1 ? spacing * 2 : 0;

    // Apply offsets to Start Position
    const startX = startCenterX + (lateralOffset * nX) + (forwardOffset * uX);
    const startY = startCenterY + (lateralOffset * nY) + (forwardOffset * uY);

    // Apply offsets to Target Position (maintain formation)
    const targetX = targetCenterX + (lateralOffset * nX) + (forwardOffset * uX);
    const targetY = targetCenterY + (lateralOffset * nY) + (forwardOffset * uY);

    // Final calculation for this specific bee
    const dirX = targetX - startX;
    const dirY = targetY - startY;
    const distance = Math.hypot(dirX, dirY);
    const speed = distance / duration;
    
    const spawnX = startX;
    const spawnY = startY;
    const normX = distance > 0 ? dirX / distance : 0;
    const normY = distance > 0 ? dirY / distance : 0;

    return {
      spawnX,
      spawnY,
      vx: normX * speed,
      vy: normY * speed,
      speed,
      duration,
    };
  }

  private deactivateBeeSparkle(instance: BeeSparkleInstance) {
    if (!instance.active) return;

    instance.active = false;
    instance.speed = 0;
    instance.vx = 0;
    instance.vy = 0;
    instance.elapsed = 0;
    instance.duration = 0;
    instance.animator.hidden = true;
    if (this.beeSparkleActiveCount > 0) {
      this.beeSparkleActiveCount -= 1;
    }
  }

  private renderBeeSparkles(ctx: CanvasRenderingContext2D) {
    if (!globalAssetsRef.current || !this.beeSparkles.length) return;

    this.beeSparkles.forEach(({ animator, active }) => {
      if (!active) return;
      animator.render(ctx, (key: string) => globalAssetsRef.current!.images[key]);
    });

  }

  private computeStaticSprites() {
    const assets = globalAssetsRef.current;
    if (!assets) return;

    // Pins and glow sprites
    {
      const offset = 200

      this.pinSprites = [];
      const pinImage = assets.images['pin'];
      const specialPinImage = assets.images['special-pin'];

      INITIALIZED_PINS.pins.forEach((pin) => {
        const isSpecial = !!pin.isSpecial;
        const radius = isSpecial ? SPECIAL_PIN_RADIUS_BASE : PIN_RADIUS_BASE;
        const image = isSpecial ? specialPinImage : pinImage;
        if (image) {
          const imageSize = radius * (isSpecial ? 3 : 5);
          const sprite = new Sprite({ x: pin.x, y: pin.y + offset, width: imageSize, height: imageSize, source: image, anchor: { x: 0.5, y: 0.5 } });
          this.pinSprites.push(sprite);
        } else {
          // No image; keep nulls to trigger fallback drawing
          this.pinSprites.push(null);
        }
      });
    }

    // Bins: holes and dividers
    {
      this.binHoleSprites = [];
      this.binDividerSprites = [];
      const holeImage = assets.images['hole'];
      const dividerImage = assets.images['hole-divider'];
      const BIN_SPACING = 10;
      const BIN_WIDTH = PIN_SPACING_X_BASE * 1 - BIN_SPACING;
      const binCount = BIN_MULTIPLIERS.length;
      const binHeight = 35;
      const topY = 800;
      const totalWidth = binCount * BIN_WIDTH + (binCount - 1) * BIN_SPACING;
      const startX = CANVAS_WIDTH / 2 - totalWidth / 2;

      if (dividerImage) {
        for (let i = 0; i < binCount - 1; i++) {
          const dividerX = startX + (i + 1) * BIN_WIDTH + (i + 0.5) * BIN_SPACING;
          const dividerWidth = dividerImage.width / 7;
          const dividerHeight = dividerImage.height / 7;
          const dividerSprite = new Sprite({
            x: dividerX - dividerWidth / 2,
            y: topY - (dividerHeight - binHeight) / 2 + 2,
            width: dividerWidth,
            height: dividerHeight,
            source: dividerImage,
            anchor: { x: 0, y: 0.1 },
          });
          this.binDividerSprites.push(dividerSprite);
        }
      }

      if (holeImage) {
        for (let i = 0; i < binCount; i++) {
          const x = startX + i * (BIN_WIDTH + BIN_SPACING);
          const y = topY + 10;
          const holeSize = BIN_WIDTH * 0.7;
          const holeSprite = new Sprite({ 
            x: x + (BIN_WIDTH - holeSize) / 2, 
            y: y + (binHeight - holeSize) / 2, 
            width: holeSize, 
            height: holeSize, 
            source: holeImage, 
            anchor: { x: 0, y: 0 } 
          });
          this.binHoleSprites.push(holeSprite);
        }
      }
    }
  }

  private renderPinsCached(ctx: CanvasRenderingContext2D, pinFx: Map<number, number>) {
    INITIALIZED_PINS.pins.forEach((pin, i) => {
      if (!pin.visible) {
        return;
      }
      
      const isSpecial = !!pin.isSpecial;
      const baseRadius = isSpecial ? SPECIAL_PIN_RADIUS_BASE : PIN_RADIUS_BASE;

      const sprite = this.pinSprites[i];
      if (!sprite) {
        // FALLBACK PIN — art assets absent (e.g. the standalone v3 screen). Draw the peg directly
        // so the board is fully playable without the sprite pipeline.
        const framesLeft0 = pinFx.get(i) ?? 0;
        const hot = framesLeft0 > 0;
        ctx.save();
        ctx.beginPath();
        ctx.arc(pin.x, pin.y + 200, baseRadius * (hot ? 1.25 : 1), 0, Math.PI * 2);
        ctx.fillStyle = isSpecial ? COLORS.yellow : (hot ? COLORS.white : 'rgba(220,235,240,0.55)');
        ctx.fill();
        ctx.restore();
        return;
      }

      // Frames left for spark (decay from PIN_SPARK_MAX_FRAMES to 0)
      const framesLeft = pinFx.get(i) ?? 0;
      const t = Math.max(0, Math.min(PIN_SPARK_MAX_FRAMES, framesLeft)) / PIN_SPARK_MAX_FRAMES;
      const imgScale = framesLeft > 0 ? 1 + (isSpecial ? 0.3 : 0.1) * t : 1;     // 120% -> 100%
      const glowScale = framesLeft > 0 ? 1 + (isSpecial ? 0.4 : 0.4) * t : 1;    // slightly larger glow
      const glowAlpha = t;                                    // strong -> fade

      // Glow (gradient-based, not using glow sprites)
      if (framesLeft > 0) {
        ctx.save();
        const innerR = Math.max(1, baseRadius * 0.2);
        const outerR = baseRadius * 3 * glowScale;
        const grad = ctx.createRadialGradient(sprite.x, sprite.y, innerR, sprite.x, sprite.y, outerR);
        const baseColor = isSpecial ? COLORS.yellow : COLORS.white;
        grad.addColorStop(0.0, baseColor);
        grad.addColorStop(0.2, 'rgba(255,255,255,0.1)');
        grad.addColorStop(1.0, 'rgba(255,255,255,0)');
        ctx.globalAlpha *= Math.min(1, 0.95 * glowAlpha);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(sprite.x, sprite.y, outerR, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      // Apply scale animation to sprite
      sprite.scale = imgScale;
      sprite.render(ctx);
      if (isSpecial) {
        ctx.save();
        ctx.shadowColor = COLORS.specialPin;
        ctx.shadowBlur = 10;
        ctx.globalAlpha *= 0.6;
        sprite.render(ctx);
        ctx.restore();
      }
    });
  }

  private renderBinsCached(ctx: CanvasRenderingContext2D) {
    const BIN_SPACING = 10;
    const BIN_WIDTH = PIN_SPACING_X_BASE * 1 - BIN_SPACING;
    const binCount = BIN_MULTIPLIERS.length;
    const topY = 800;
    const totalWidth = binCount * BIN_WIDTH + (binCount - 1) * BIN_SPACING;
    const startX = CANVAS_WIDTH / 2 - totalWidth / 2;

    // Dividers
    if (this.binDividerSprites.length) {
      this.binDividerSprites.forEach((s) => s.render(ctx));
    }

    // Bins (holes) with tint overlay if hit
    for (let i = 0; i < binCount; i++) {
      const x = startX + i * (BIN_WIDTH + BIN_SPACING);
      const y = topY + 10;
      const isHardMain = this.hardMode && this.mode === 'main';
      const displayMultiplier = isHardMain ? '0' : getBinLabel(i);
      ctx.save();
      // if (isHit) {
      //   ctx.globalAlpha = 0.8;
      //   ctx.fillStyle = COLORS.yellow;
      //   ctx.fillRect(x, y, BIN_WIDTH, binHeight);
      //   ctx.globalAlpha = 1;
      // }
      if (this.binHoleSprites[i]) {
        this.binHoleSprites[i].render(ctx);
      }
      ctx.restore();

      // Light beams moved to separate method for layering check

      // Multiplier text (hide in hard-mode main stage)
      if (!isHardMain) {
        ctx.save();
        ctx.fillStyle = COLORS.white;
        ctx.globalAlpha *= 0.4;
        ctx.font = `bold ${16}px Gabarito`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(displayMultiplier + 'x', x + BIN_WIDTH / 2, y);
        ctx.restore();
      }
    }
  }

  private prepareBinLightBeams() {
    this.binLightBeams = [];
    // Ensure we only create beams if the asset definition exists, or check internally
    if (!globalAssetsRef.current?.customAnimations?.['light-beam']) return;
    
    // We can just query keys from globalAssetsRef or hardcode them if we know our logic
    // const animSet = globalAssetsRef.current.customAnimations['light-beam']; // Removed

    const BIN_SPACING = 10;
    const BIN_WIDTH = PIN_SPACING_X_BASE * 1 - BIN_SPACING;
    const binCount = BIN_MULTIPLIERS.length;
    const topY = 600;
    const totalWidth = binCount * BIN_WIDTH + (binCount - 1) * BIN_SPACING;
    const startX = CANVAS_WIDTH / 2 - totalWidth / 2;

    const anims = ['glow3', 'glow2', 'glow2', 'glow1', 'glow1', 'glow1', 'glow1', 'glow2', 'glow2', 'glow3'];

    for (let i = 0; i < binCount; i++) {
      const x = startX + i * (BIN_WIDTH + BIN_SPACING);
      const y = topY + 40;
      const centerX = x + BIN_WIDTH / 2 + 5;
      const centerY = y;
      
      const animName = anims[i] || 'glow1';
      const anim = new CustomSpriteAnimator('light-beam');
      anim.x = centerX;
      anim.y = centerY;
      anim.play(animName); 
      this.binLightBeams.push(anim);
    }
  }

  private prepareBeeSparkles() {
    this.beeSparkles = [];
    this.beeSparkleLength = 0;
    this.beeSparklePendingBursts = [];
    this.beeSparkleActiveCount = 0;
    
    // We still check existence to ensure we don't init without data, or we could just let the animator handle empty
    // const set = globalAssetsRef.current?.customAnimations?.['bee-sparkling']; // Removed
    if (!globalAssetsRef.current?.customAnimations?.['bee-sparkling']) return; // Kept check for asset existence

    const renderScale = CANVAS_HEIGHT / 1080;
    // We can rely on the animator, but here we wanted idleFrames for size?
    // Let's assume standard size for now or fetch from global inside if needed. 
    // Actually current logic uses 'set' for nothing else than existence check and 'idle' frames check.
    // Let's simplify and rely on the animator.
    
    // Removed logic for beeSparkleIdleAnimation, idleKey, and modifying set[idleKey].loop
    // Removed sizeSource and beeSparkleLength calculation as it depended on 'set'

    for (let i = 0; i < this.beeSparklePoolSize; i++) {
      const animator = new CustomSpriteAnimator('bee-sparkling'); // Removed 'set'
      animator.scale = renderScale; 
      animator.hidden = true;
      this.beeSparkles.push({ animator, active: false, speed: 0, vx: 0, vy: 0, elapsed: 0, duration: 0 });
    }
  }

  private renderBinLightBeams(ctx: CanvasRenderingContext2D, hitBins: Set<number>) {
    if (!this.binLightBeams.length) return;
    
    this.binLightBeams.forEach((beam, i) => {
        if (!beam) return;
        const isHit = hitBins.has(i);
        const current = beam.currentAnimationName;
        
        if (isHit) {
             beam.play(current!, true);
        }
        
        beam.render(ctx, (key: string) => globalAssetsRef.current!.images[key], 0.95);
    });
  }

}
